import { DateTime } from 'luxon'

/** One release in rt2-app/whats-new.md, as GET app_settings/whats_new sends it (newest first). */
export interface WhatsNewEntry {
  readonly version: string
  /** The header's date, normally yyyy-MM-dd; null when the header has none. */
  readonly date: string | null
  readonly notes: readonly string[]
}

/** Dotted version compare: negative when a < b. Missing parts count as 0, so 2.0 equals 2.0.0. */
export function compareVersions(a: string, b: string): number {
  const pa = String(a ?? '').split('.').map(Number)
  const pb = String(b ?? '').split('.').map(Number)
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0)
    if (d) return d
  }
  return 0
}

/**
 * The entries this build may show: those at or below its own version, newest first. The file on the server is
 * shared by every environment, so it can already name a version this device has not updated to yet.
 */
export function released(entries: readonly WhatsNewEntry[], appVersion: string): WhatsNewEntry[] {
  return entries
    .filter(e => compareVersions(e.version, appVersion) <= 0)
    .sort((a, b) => compareVersions(b.version, a.version))
}

/**
 * The released entries newer than the version this device last acknowledged; what the after-update sheet shows.
 * With nothing acknowledged yet (an upgrade from a build that predates the notes) only the newest entry: the
 * version just installed, not the whole history.
 */
export function unseen(entries: readonly WhatsNewEntry[], appVersion: string, lastSeen: string | null): WhatsNewEntry[] {
  const list = released(entries, appVersion)
  return lastSeen ? list.filter(e => compareVersions(e.version, lastSeen) > 0) : list.slice(0, 1)
}

/** "Nov 5, 2026" from the header's date; the raw text when it is not a date; '' for none. */
export function entryDate(date: string | null | undefined): string {
  if (!date) return ''
  const d = DateTime.fromISO(date)
  return d.isValid ? d.toFormat('LLL d, yyyy') : date
}
